import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const T = {
  bg:        '#060d1e',
  surface:   '#0d1a2d',
  surfaceHi: '#132238',
  border:    '#1e3a5f',
  borderHi:  '#2d5a8e',
  text:      '#f0f6ff',
  textSub:   '#8ba5c8',
  textMute:  '#4a6a8a',
  emerald:   '#10b981',
  emeraldDim:'rgba(16,185,129,0.10)',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
  teal:      '#2dd4bf',
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysUntil = (d) => {
  if (!d) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.ceil((new Date(d) - today) / (1000 * 60 * 60 * 24));
};

export default function EarningsCollider({ session, plan }) {
  const [earnings, setEarnings] = useState([]);
  const [fdaEvents, setFdaEvents] = useState([]);
  const [collisions, setCollisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('collisions');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  useEffect(() => {
    fetchData();
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function fetchData() {
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    const [earningsRes, fdaRes] = await Promise.all([
      supabase.from('earnings_calendar').select('*').gte('earnings_date', today).order('earnings_date'),
      supabase.from('fda_calendar').select('*').eq('status', 'pending').gte('decision_date', today).order('decision_date'),
    ]);
    const earningsList = earningsRes.data || [];
    const fdaList = fdaRes.data || [];
    setEarnings(earningsList);
    setFdaEvents(fdaList);

    const found = [];
    for (const e of earningsList) {
      const eDate = new Date(e.earnings_date + 'T00:00:00');
      const weekStart = new Date(eDate); weekStart.setDate(eDate.getDate() - eDate.getDay());
      const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
      const eTicker = e.ticker.toUpperCase();
      const eCompany = (e.company_name || '').toLowerCase();
      for (const f of fdaList) {
        if (!f.decision_date) continue;
        const fDate = new Date(f.decision_date + 'T00:00:00');
        const fCompany = (f.company || '').toLowerCase();
        const sameWeek = fDate >= weekStart && fDate <= weekEnd;
        const tickerMatch = fCompany.includes(eTicker.toLowerCase());
        const nameMatch = eCompany.split(' ').some(w => w.length > 3 && fCompany.includes(w));
        if (sameWeek && (tickerMatch || nameMatch)) {
          found.push({
            ticker: eTicker, company: e.company_name,
            earnings_date: e.earnings_date, fda_drug: f.drug_name,
            fda_date: f.decision_date, fda_indication: f.indication,
            days_apart: Math.abs(Math.ceil((fDate - eDate) / (1000*60*60*24))),
            week_label: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' – ' + weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          });
        }
      }
    }
    setCollisions(found);
    setLoading(false);
  }

  const upcomingEarnings = earnings.filter(e => { const d = daysUntil(e.earnings_date); return d !== null && d >= 0; });
  const byWeek = {};
  for (const e of upcomingEarnings) {
    const d = new Date(e.earnings_date + 'T00:00:00');
    const ws = new Date(d); ws.setDate(d.getDate() - d.getDay());
    const key = ws.toISOString().split('T')[0];
    if (!byWeek[key]) byWeek[key] = { weekStart: ws, items: [] };
    byWeek[key].items.push(e);
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.text, margin: '0 0 6px', letterSpacing: '-0.5px' }}>
          Earnings + FDA Collision Detector
        </h2>
        <p style={{ color: T.textSub, fontSize: isMobile ? 14 : 16, margin: 0, lineHeight: 1.6 }}>
          {isMobile
            ? 'When earnings and an FDA decision land in the same week — high volatility.'
            : "When a biotech company has both an earnings call and an FDA decision in the same week — that's a high-volatility event. Track it here."}
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Earnings Events', value: upcomingEarnings.length,                                               color: T.blue    },
          { label: 'FDA Decisions',   value: fdaEvents.length,                                                      color: T.red     },
          { label: 'Collision Weeks', value: collisions.length,                                                     color: T.amber   },
          { label: 'This Month',      value: upcomingEarnings.filter(e => daysUntil(e.earnings_date) <= 30).length, color: T.emerald },
        ].map((s, i) => (
          <div key={i} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: isMobile ? '14px 16px' : '18px 22px' }}>
            <div style={{ fontSize: 11, color: T.textMute, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.label}</div>
            <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 800, color: s.color, lineHeight: 1 }}>{loading ? '—' : s.value}</div>
          </div>
        ))}
      </div>

      {/* View Toggle */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24, borderBottom: `1px solid ${T.border}` }}>
        {[
          { id: 'collisions', label: `Collision Weeks (${collisions.length})`, color: T.amber },
          { id: 'all',        label: `All Earnings (${upcomingEarnings.length})`, color: T.blue },
        ].map(v => {
          const isActive = view === v.id;
          return (
            <button key={v.id} onClick={() => setView(v.id)} style={{
              padding: isMobile ? '10px 14px 9px' : '11px 20px 10px',
              border: 'none', borderBottom: `2px solid ${isActive ? v.color : 'transparent'}`,
              background: 'transparent', color: isActive ? v.color : T.textSub,
              cursor: 'pointer', fontSize: isMobile ? 13 : 15, fontWeight: isActive ? 700 : 400,
              transition: 'all 0.15s', marginBottom: '-1px', whiteSpace: 'nowrap',
            }}>{v.label}</button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMute }}>
          <div style={{ fontSize: 15, marginBottom: 12 }}>Loading earnings data...</div>
          <div style={{ width: 80, height: 2, background: T.border, borderRadius: 1, overflow: 'hidden', margin: '0 auto' }}>
            <div style={{ height: '100%', width: '50%', background: T.emerald, animation: 'loadBar 1.4s ease-in-out infinite' }} />
          </div>
        </div>
      ) : view === 'collisions' ? (
        collisions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: isMobile ? 40 : 60, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 }}>
            <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 700, color: T.textSub, marginBottom: 10 }}>No Collision Weeks Right Now</div>
            <div style={{ fontSize: 15, color: T.textMute, maxWidth: 360, margin: '0 auto', lineHeight: 1.7 }}>
              Collisions are rare and high-signal. When a company has both earnings and an FDA decision in the same week, it will appear here automatically.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {collisions.map((c, i) => (
              <div key={i} style={{
                background: T.surface,
                border: `1px solid rgba(245,158,11,0.3)`,
                borderLeft: `3px solid ${T.amber}`,
                borderRadius: 14, padding: isMobile ? 16 : 22,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 800, color: T.amber, letterSpacing: '0.5px' }}>HIGH VOLATILITY WEEK</span>
                  <span style={{ fontSize: 12, color: T.textMute, marginLeft: 'auto' }}>
                    {isMobile ? c.week_label.split(' – ')[0] : `Week of ${c.week_label}`}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                  <div style={{ background: T.blueDim, border: `1px solid rgba(59,130,246,0.2)`, borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.blue, letterSpacing: '0.8px', marginBottom: 10 }}>EARNINGS CALL</div>
                    <div style={{ fontSize: isMobile ? 17 : 18, fontWeight: 800, color: T.emerald, marginBottom: 4 }}>{c.ticker}</div>
                    <div style={{ fontSize: 14, color: T.textSub, marginBottom: 8 }}>{c.company}</div>
                    <div style={{ fontSize: 14, color: T.blue, fontWeight: 600 }}>{fmtDate(c.earnings_date)}</div>
                    <div style={{ fontSize: 12, color: T.textMute, marginTop: 3 }}>
                      {daysUntil(c.earnings_date) === 0 ? 'TODAY' : daysUntil(c.earnings_date) === 1 ? 'Tomorrow' : `In ${daysUntil(c.earnings_date)} days`}
                    </div>
                  </div>
                  <div style={{ background: 'rgba(239,68,68,0.06)', border: `1px solid rgba(239,68,68,0.2)`, borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.red, letterSpacing: '0.8px', marginBottom: 10 }}>FDA DECISION</div>
                    <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>{c.fda_drug?.length > (isMobile ? 30 : 40) ? c.fda_drug.substring(0, isMobile ? 30 : 40) + '...' : c.fda_drug}</div>
                    {c.fda_indication && <div style={{ fontSize: 13, color: T.textSub, marginBottom: 8 }}>{c.fda_indication.substring(0, isMobile ? 50 : 60)}</div>}
                    <div style={{ fontSize: 14, color: T.red, fontWeight: 600 }}>{fmtDate(c.fda_date)}</div>
                    <div style={{ fontSize: 12, color: T.textMute, marginTop: 3 }}>
                      {daysUntil(c.fda_date) === 0 ? 'TODAY' : daysUntil(c.fda_date) === 1 ? 'Tomorrow' : `In ${daysUntil(c.fda_date)} days`}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 14, padding: '12px 16px', background: 'rgba(245,158,11,0.05)', border: `1px solid rgba(245,158,11,0.15)`, borderRadius: 10, fontSize: isMobile ? 13 : 14, color: T.textSub, lineHeight: 1.7 }}>
                  <strong style={{ color: T.amber }}>Why this matters:</strong> Earnings and FDA decisions in the same week create compounded volatility. A positive FDA ruling can send stocks up 50%+. A rejection can cause double-digit drops — all while earnings sentiment adds fuel.
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {Object.entries(byWeek).map(([weekKey, { weekStart, items }]) => {
            const weekLabel = weekStart.toLocaleDateString('en-US', { month: isMobile ? 'short' : 'long', day: 'numeric', year: 'numeric' });
            return (
              <div key={weekKey}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.textSub, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 3, height: 16, background: T.border, borderRadius: 2 }} />
                  Week of {weekLabel}
                  <span style={{ fontSize: 13, color: T.textMute }}>· {items.length} reporting</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fill,minmax(220px,1fr))', gap: 8 }}>
                  {items.map((e, i) => {
                    const days = daysUntil(e.earnings_date);
                    const isToday = days === 0;
                    const isTomorrow = days === 1;
                    return (
                      <div key={i} style={{
                        background: T.surface,
                        border: `1px solid ${isToday ? `${T.amber}40` : T.border}`,
                        borderLeft: `3px solid ${isToday ? T.amber : T.blue}`,
                        borderRadius: 10, padding: isMobile ? '12px 14px' : '14px 16px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                          <span style={{ fontSize: isMobile ? 15 : 16, fontWeight: 800, color: T.emerald }}>{e.ticker}</span>
                          {isToday    && <span style={{ fontSize: 10, fontWeight: 700, color: T.amber, background: 'rgba(245,158,11,0.1)', padding: '2px 7px', borderRadius: 4 }}>TODAY</span>}
                          {isTomorrow && <span style={{ fontSize: 10, fontWeight: 700, color: T.textSub, background: T.surfaceHi, padding: '2px 7px', borderRadius: 4 }}>TMR</span>}
                        </div>
                        <div style={{ fontSize: 12, color: T.textSub, marginBottom: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.company_name}</div>
                        <div style={{ fontSize: 13, color: T.textMute }}>{fmtDate(e.earnings_date)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 28, padding: '14px 18px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10 }}>
        <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.7 }}>
          <strong style={{ color: T.text }}>Source:</strong> Earnings dates from Alpha Vantage. FDA decisions from our FDA Calendar. Always verify independently. Not financial advice.
        </div>
      </div>

      <style>{`@keyframes loadBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
    </div>
  );
}